import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import {
  getRuntimeSelectedProfileId,
  resolveInitialSelectedProfileId,
  setSelectedProfileId as persistSelectedProfileId,
} from "@/lib/selectedProfile";

/** @returns {any} */
export function useProfiles() {
  const queryClient = useQueryClient();

  const [selectedProfileId, setSelectedProfileId] = useState(
    () => getRuntimeSelectedProfileId()
  );

  // Fetch all profiles
  const {
    data: profiles = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["profiles"],
    queryFn: () => api.profiles.list().then((res) => res.data.profiles),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Derive selectedProfile from list
  const selectedProfile =
    profiles.find((p) => p.id === selectedProfileId) || null;

  useEffect(() => {
    const enterableIds = profiles.map((p) => p.id);
    const resolved = resolveInitialSelectedProfileId(enterableIds);
    if (resolved && resolved !== selectedProfileId) {
      setSelectedProfileId(resolved);
      return;
    }
    if (!resolved && profiles.length > 0) {
      selectProfile(profiles[0].id);
    }
  }, [profiles, selectedProfileId]);

  const selectProfile = useCallback((id) => {
    const next = persistSelectedProfileId(id);
    setSelectedProfileId(next);
  }, []);

  // Create
  /**
   * @param {import('@/api/client').CreateProfileRequest} data
   */
  const createProfileFn = (data) =>
    api.profiles.create(data).then((r) => r.data.profile);
  const createMutation = useMutation({
    mutationFn: createProfileFn,
    onSuccess: (newProfile) => {
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
      selectProfile(newProfile.id);
    },
  });

  // Update
  /**
   * @param {{ id: string, data: Partial<import('@/api/client').CreateProfileRequest> }} args
   */
  const updateProfileFn = ({ id, data }) =>
    api.profiles.update(id, data).then((r) => r.data.profile);
  const updateMutation = useMutation({
    mutationFn: updateProfileFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
    },
  });

  // Delete (soft)
  /** @param {string} id */
  const deleteProfileFn = (id) => api.profiles.delete(id);
  const deleteMutation = useMutation({
    mutationFn: deleteProfileFn,
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
      if (selectedProfileId === deletedId) {
        selectProfile(null);
      }
    },
  });

  return {
    profiles,
    selectedProfile,
    selectedProfileId,
    isLoading,
    isFetching,
    error,
    refetch,
    selectProfile,
    createProfile: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
    updateProfile: (id, data) => updateMutation.mutateAsync({ id, data }),
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    deleteProfile: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
